/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { compact } from "lodash"
import {
  FoodmaxapipbGetTranslationResponse,
  FoodmaxapipbListAttributesResponse,
  FoodmaxapipbSearchItemsResponse,
  FoodmaxapipbUpdateItemAvailableStatusV2Response,
} from "generated/foodmaxapi.type"
import { PARTIAL_MENU_REFRESH_TAGS, partialMenuApi } from "queries"
import {
  CreateSectionRtkRequest,
  CreateCategoryRtkRequest,
  FetchAttributeClusterOptionRequest,
  FetchListBrandsSearchRequest,
  FetchSupportedAttributeClusterRequest,
  GetTranslationRequest,
  PostFeedbackGrabCategoryRequest,
  SearchItemsRequest,
  SortDepartmentsRequest,
  SortCategoriesRequest,
  SortDepartmentItemsRequest,
  SortItemsRequest,
  UpdateExistingTranslationRequest,
  UpdateItemsAvailableV1Request,
} from "queries/types"
import { SortableResource } from "store/duck/menu.type"
import {
  AxiosAction,
  CreateCategoryRequest,
  CreateCategoryResponse,
  CreateSectionRequest,
  CreateSectionResponse,
  ResponseWithError,
} from "types/api"
import { FetchTranslationResponse } from "types/api/menuFetchTranslation.type"
import {
  Item,
  MenuTranslationRequestData,
  PostPredictionFeedbacksGrabCategory,
} from "types/models/menu"
import {
  FetchListAttributeOptionsResponse,
  FetchSupportedAttributeClusterResponse,
} from "types/api/menuFetchAttributeOptions.type"
import { getActionSuccess, getActionFail } from "store/duck/utils/actionUtils"
import { actions } from "store/duck/menu"
import { actionCreator } from "store/store"
import { actionTypes } from "store/duck/bulkUpload/actions"
import { generateAutoTranslationQueueKeys } from "utils/translation_helper"
import { TroyWebTracker } from "utils/tracking"
import { EventActionEng, EventCategoryEng } from "constants/tracking"
import notification from "components/common/notification"
import {
  GetBulkUploadTaskRequest,
  GetBulkUploadTaskResponse,
} from "types/delv-platform-api/bulkUploadTask"

interface RtkMigrationBaseOptions {
  dispatch: (action: unknown) => unknown | Promise<unknown>
  merchantID: string
  merchantGroupID: string
  menuGroupID?: string
  isRtkFlagLoading: boolean
}

/** RTK lazy `fetchSupportedAttributeCluster` trigger result shape */
export type FetchSupportedAttributeClusterRtkResult = {
  data?: FetchSupportedAttributeClusterResponse
  error?: unknown
}

export type FetchAttributeClusterOptionRtkResult = {
  data?: FetchListAttributeOptionsResponse
  error?: unknown
}

export interface FetchAttributeClusterOptionWithMigrationOptions {
  attributeClusterID: string
  dispatch: (action: unknown) => unknown | Promise<unknown>
  dispatchLegacyFetch: () => unknown | Promise<unknown>
  fetchAttributeClusterOptionRtk: (
    arg: FetchAttributeClusterOptionRequest,
  ) => PromiseLike<FetchAttributeClusterOptionRtkResult>
  isMenuGroup: boolean
  isRtkFetchAttributeClusterOptionEnabled: boolean
  menuGroupID?: string
  merchantGroupID: string
  merchantID: string
  queryParams: Pick<
    FetchAttributeClusterOptionRequest,
    "attributeClusterIDs" | "page" | "name" | "locale"
  >
}

type FetchListBrandsSearchRtkResult = {
  data?: FoodmaxapipbListAttributesResponse
  error?: unknown
}

export interface FetchListBrandsSearchWithMigrationOptions {
  dispatch: (action: unknown) => unknown | Promise<unknown>
  dispatchLegacyFetch: () => unknown | Promise<unknown>
  fetchListBrandsSearchRtk: (
    arg: FetchListBrandsSearchRequest,
  ) => PromiseLike<FetchListBrandsSearchRtkResult>
  isRtkFetchListBrandsSearchEnabled: boolean
  merchantID: string
  queryParams: Omit<FetchListBrandsSearchRequest, "merchantID">
}

export function fetchListBrandsSearchWithMigration({
  dispatch,
  dispatchLegacyFetch,
  fetchListBrandsSearchRtk,
  isRtkFetchListBrandsSearchEnabled,
  merchantID,
  queryParams,
}: FetchListBrandsSearchWithMigrationOptions): void {
  const sendTracking = (fromRtkQuery: boolean) => {
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.fetchListBrandsSearch,
      labels: {
        MERCHANT_ID: merchantID,
        IS_RTK_ENABLED: isRtkFetchListBrandsSearchEnabled,
        FROM_RTK_QUERY: fromRtkQuery,
        FROM_OLD_REDUX: !fromRtkQuery,
        ACTION_NAME: "fetchListBrandsSearch",
      },
    })
  }

  if (!isRtkFetchListBrandsSearchEnabled) {
    Promise.resolve(dispatchLegacyFetch())
    sendTracking(false)
    return
  }

  const legacyAction = actionCreator.menu.fetchListBrandsSearch(
    merchantID,
    queryParams,
  )
  const pendingAction = {
    type: actions.FETCH_LIST_BRANDS_SEARCH,
    params: queryParams,
  }
  const previousAction = {
    ...legacyAction,
    // axiosBaseQuery has already reported this failure.
    notTrackError: true,
  }

  dispatch(pendingAction)
  fetchListBrandsSearchRtk({ merchantID, ...queryParams }).then(result => {
    if ("error" in result) {
      const error = result.error as {
        data?: unknown
        headers?: Record<string, string>
        status?: number | string
      }
      dispatch({
        type: getActionFail(actions.FETCH_LIST_BRANDS_SEARCH),
        payload: { data: {} },
        error: {
          response: {
            data: error.data,
            status: typeof error.status === "number" ? error.status : undefined,
            headers: error.headers ?? {},
            config: legacyAction.payload.request,
          },
        },
        meta: { previousAction },
      })
    } else {
      dispatch({
        type: getActionSuccess(actions.FETCH_LIST_BRANDS_SEARCH),
        payload: { data: result.data },
        meta: { previousAction },
      })
    }
    sendTracking(true)
  })
}

/**
 * Fetches attribute-cluster options using RTK Query or legacy Redux while
 * preserving the legacy reducer lifecycle and migration instrumentation.
 */
export async function fetchAttributeClusterOption(
  options: FetchAttributeClusterOptionWithMigrationOptions,
): Promise<void> {
  const {
    attributeClusterID,
    dispatch,
    dispatchLegacyFetch,
    fetchAttributeClusterOptionRtk,
    isMenuGroup,
    isRtkFetchAttributeClusterOptionEnabled,
    menuGroupID,
    merchantGroupID,
    merchantID,
    queryParams,
  } = options

  const sendTracking = (fromRtkQuery: boolean) => {
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.fetchAttributeClusterOption,
      labels: {
        MERCHANT_ID: merchantID,
        IS_RTK_ENABLED: isRtkFetchAttributeClusterOptionEnabled,
        FROM_RTK_QUERY: fromRtkQuery,
        FROM_OLD_REDUX: !fromRtkQuery,
        ACTION_NAME: "fetchAttributeClusterOption",
      },
    })
  }

  if (isRtkFetchAttributeClusterOptionEnabled) {
    const pendingAction = {
      type: actions.FETCH_ATTRIBUTE_CLUSTER_OPTION,
      attributeClusterID,
    }
    const requestUrl = `/api/food/merchant${
      isMenuGroup ? "/v2/menu-groups/attributes" : "/v1/attributes"
    }`
    const previousAction = {
      ...pendingAction,
      // axiosBaseQuery has already sent RTK failures to monitoring.
      notTrackError: true,
      payload: { request: { url: requestUrl, params: queryParams } },
    }

    dispatch(pendingAction)
    const result = await fetchAttributeClusterOptionRtk({
      merchantID,
      merchantGroupID,
      isMenuGroup,
      menuGroupID,
      ...queryParams,
    })

    dispatch(
      "error" in result
        ? {
            type: getActionFail(actions.FETCH_ATTRIBUTE_CLUSTER_OPTION),
            payload: { data: {} },
            meta: { previousAction },
          }
        : {
            type: getActionSuccess(actions.FETCH_ATTRIBUTE_CLUSTER_OPTION),
            payload: { data: result.data },
            meta: { previousAction },
          },
    )
    sendTracking(true)
  } else {
    await Promise.resolve(dispatchLegacyFetch())
    sendTracking(false)
  }
}

/** RTK lazy `searchItems` trigger result shape used by menu item search flows */
export type MenuItemSearchRtkResult = {
  data?: FoodmaxapipbSearchItemsResponse
  error?: unknown
}

/** RTK `getTranslation` mutation trigger result shape */
export type GetTranslationRtkResult = {
  data?: FoodmaxapipbGetTranslationResponse
  error?: unknown
}

/** RTK `updateExistingTranslation` mutation trigger result shape */
export type UpdateExistingTranslationRtkResult = {
  data?: FetchTranslationResponse
  error?: unknown
}

export type CreateSectionRtkResult = {
  data?: CreateSectionResponse
  error?: unknown
}

export type CreateCategoryRtkResult = {
  data?: CreateCategoryResponse
  error?: unknown
}

export type SortDepartmentsRtkResult = {
  data?: unknown
  error?: unknown
}

export type SortItemsRtkResult = {
  data?: unknown
  error?: unknown
}

export type SortCategoriesRtkResult = {
  data?: unknown
  error?: unknown
}

export type SortDepartmentItemsRtkResult = {
  data?: unknown
  error?: unknown
}

export interface PerformMenuItemSearchOptions extends RtkMigrationBaseOptions {
  /** Matches `showLoading` on the pending SEARCH_ITEMS action and legacy thunk */
  showLoading: boolean
  /** Base action type, e.g. `actions.SEARCH_ITEMS` from the menu duck */
  searchItemsBaseAction: string
  isRtkSearchItemsEnabled: boolean
  searchItemsRtk: (arg: SearchItemsRequest) => Promise<MenuItemSearchRtkResult>
  dispatchLegacySearch: () => unknown | Promise<unknown>
}

export interface FetchGetTranslationOptions extends RtkMigrationBaseOptions {
  /** Base action type, e.g. `actions.GET_TRANSLATION` from the menu duck */
  getTranslationBaseAction: string
  data: MenuTranslationRequestData
  isRtkGetTranslationEnabled: boolean
  getTranslationRtk: (
    arg: GetTranslationRequest,
  ) => PromiseLike<GetTranslationRtkResult>
  dispatchLegacyGetTranslation: () => unknown | Promise<unknown>
}

export interface FetchUpdateExistingTranslationOptions {
  dispatch: (action: unknown) => unknown | Promise<unknown>
  merchantID: string
  merchantGroupID: string
  menuGroupID?: string
  data: MenuTranslationRequestData
  isRtkUpdateExistingTranslationEnabled: boolean
  updateExistingTranslationRtk: (
    arg: UpdateExistingTranslationRequest,
  ) => PromiseLike<UpdateExistingTranslationRtkResult>
}

export interface FetchSortDepartmentsOptions {
  dispatch: (action: unknown) => unknown | Promise<unknown>
  merchantID: string
  merchantGroupID: string
  menuGroupID?: string
  parentDepartmentID?: string | null
  sorts: SortableResource[]
  successMessage: string
  isMartDepartmentStructure: boolean
  isSellingTimeStructure: boolean
  enablePartialMenu?: boolean
  isRtkSortDepartmentsEnabled: boolean
  sortDepartmentsRtk: (
    arg: SortDepartmentsRequest,
  ) => PromiseLike<SortDepartmentsRtkResult>
}

export interface FetchSortItemsOptions {
  dispatch: (action: unknown) => unknown | Promise<unknown>
  merchantID: string
  merchantGroupID: string
  menuGroupID?: string
  categoryID: string
  sorts: SortableResource[]
  successMessage: string
  isMartDepartmentStructure: boolean
  isSellingTimeStructure: boolean
  enablePartialMenu?: boolean
  isRtkSortItemsEnabled: boolean
  sortItemsRtk: (arg: SortItemsRequest) => PromiseLike<SortItemsRtkResult>
}

export interface FetchSortCategoriesOptions {
  dispatch: (action: unknown) => unknown | Promise<unknown>
  merchantID: string
  merchantGroupID: string
  menuGroupID?: string
  sectionID: string
  sorts: SortableResource[]
  successMessage: string
  isMartDepartmentStructure: boolean
  isSellingTimeStructure: boolean
  enablePartialMenu?: boolean
  isRtkSortCategoriesEnabled: boolean
  sortCategoriesRtk: (
    arg: SortCategoriesRequest,
  ) => PromiseLike<SortCategoriesRtkResult>
}

export interface FetchSortDepartmentItemsOptions {
  dispatch: (action: unknown) => unknown | Promise<unknown>
  merchantID: string
  merchantGroupID: string
  menuGroupID?: string
  departmentID: string
  sorts: SortableResource[]
  successMessage: string
  isMartDepartmentStructure: boolean
  isSellingTimeStructure: boolean
  enablePartialMenu?: boolean
  isRtkSortDepartmentItemsEnabled: boolean
  sortDepartmentItemsRtk: (
    arg: SortDepartmentItemsRequest,
  ) => PromiseLike<SortDepartmentItemsRtkResult>
}

export interface RefreshMenuAndCloseDrawersOptions {
  dispatch: (action: unknown) => unknown | Promise<unknown>
  merchantID: string
  merchantGroupID: string
  menuGroupID?: string
  isMartDepartmentStructure: boolean
  isSellingTimeStructure: boolean
  enablePartialMenu?: boolean
  skipPartialMenuInvalidation?: boolean
}

export function refreshMenuAndCloseDrawers(
  options: RefreshMenuAndCloseDrawersOptions,
): void {
  const {
    dispatch,
    merchantID,
    merchantGroupID,
    menuGroupID,
    isMartDepartmentStructure,
    isSellingTimeStructure,
    enablePartialMenu,
    skipPartialMenuInvalidation,
  } = options

  if (!isMartDepartmentStructure) {
    dispatch(
      actionCreator.menu.fetchMenu({
        merchantID,
        isRefreshing: true,
        isSellingTimeStructure,
        merchantGroupID,
        menuGroupID,
      }),
    )
  } else if (enablePartialMenu) {
    if (!skipPartialMenuInvalidation) {
      dispatch(partialMenuApi.util.invalidateTags(PARTIAL_MENU_REFRESH_TAGS))
    }
  } else {
    dispatch(
      actionCreator.menu.fetchCatalogue({
        merchantID,
        isRefreshing: true,
        isSellingTimeStructure,
        merchantGroupID,
        menuGroupID,
      }),
    )
  }

  dispatch(actionCreator.bulkSelectionsV3.reset())
  dispatch(
    actionCreator.menu.toggleDrawer({
      showSection: false,
      showCategory: false,
      showSubDepartment: false,
      showItem: false,
      showSortCategories: false,
      showSortItems: false,
      showSortDepartments: false,
      showSortSubDepartments: false,
      showSellingTimeEditor: false,
      showMultiSelectSellingTime: false,
      showItemSelection: false,
      itemSelections: {},
    }),
  )
}

export interface FetchCreateSectionOptions {
  dispatch: (action: unknown) => unknown | Promise<unknown>
  merchantID: string
  merchantGroupID: string
  menuGroupID?: string
  data: CreateSectionRequest
  successMessage: string
  errorCallback?(response: ResponseWithError): unknown
  getErrorNotificationContent?: CreateSectionRtkRequest["getErrorNotificationContent"]
  isRtkCreateSectionEnabled: boolean
  createSectionRtk: (
    arg: CreateSectionRtkRequest,
  ) => PromiseLike<CreateSectionRtkResult>
}

export type FetchCreateSectionResult =
  | {
      fromRtkQuery: true
      result: CreateSectionRtkResult
    }
  | {
      fromRtkQuery: false
      result: AxiosAction<CreateSectionResponse>
    }

export interface FetchCreateCategoryOptions {
  dispatch: (action: unknown) => unknown | Promise<unknown>
  merchantID: string
  merchantGroupID: string
  menuGroupID?: string
  data: CreateCategoryRequest
  isSellingTimeStructure: boolean
  successOptions: {
    message: string
    description?: string
  }
  errorCallback?(response: ResponseWithError): unknown
  getErrorNotificationContent?: CreateCategoryRtkRequest["getErrorNotificationContent"]
  isRtkCreateCategoryEnabled: boolean
  createCategoryRtk: (
    arg: CreateCategoryRtkRequest,
  ) => PromiseLike<CreateCategoryRtkResult>
}

export type FetchCreateCategoryResult =
  | {
      fromRtkQuery: true
      result: CreateCategoryRtkResult
    }
  | {
      fromRtkQuery: false
      result: AxiosAction<CreateCategoryResponse>
    }

/**
 * Runs menu item search via RTK Query or legacy Redux, dispatching hand-rolled
 * SEARCH_ITEMS_SUCCESS / SEARCH_ITEMS_FAIL shapes for the RTK path so they stay aligned with the reducer.
 */
export async function performMenuItemSearch(
  searchKey: string,
  options: PerformMenuItemSearchOptions,
): Promise<void> {
  const {
    showLoading,
    searchItemsBaseAction,
    dispatch,
    merchantID,
    merchantGroupID,
    isRtkFlagLoading,
    isRtkSearchItemsEnabled,
    searchItemsRtk,
    dispatchLegacySearch,
  } = options

  if (!isRtkFlagLoading && isRtkSearchItemsEnabled) {
    const actionSearchItems = {
      type: searchItemsBaseAction,
      showLoading,
    }

    dispatch(actionSearchItems)

    const result = await searchItemsRtk({
      merchantID,
      merchantGroupID,
      searchKey,
      needModifierGroups: true,
    })

    if (result.error) {
      dispatch({
        type: getActionFail(searchItemsBaseAction),
        payload: { data: {} },
        meta: {
          previousAction: {
            ...actionSearchItems,
            payload: { request: { data: {} } },
          },
        },
      })
    } else {
      // Always dispatch success when there is no error so searchItemLoading clears.
      // RTK can resolve with neither error nor truthy data (e.g. empty body); `else if (result.data)` would leave loading stuck.
      dispatch({
        type: getActionSuccess(searchItemsBaseAction),
        payload: {
          data: result?.data ?? {},
        },
        meta: {
          previousAction: {
            ...actionSearchItems,
            payload: { request: { data: {} } },
          },
        },
      })
    }

    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.searchItems,
      labels: {
        MERCHANT_ID: merchantID || "",
        IS_RTK_ENABLED: isRtkSearchItemsEnabled,
        FROM_RTK_QUERY: true,
        FROM_OLD_REDUX: false,
        ACTION_NAME: "searchItems",
      },
    })
  } else if (!isRtkFlagLoading) {
    await Promise.resolve(dispatchLegacySearch())

    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.searchItems,
      labels: {
        MERCHANT_ID: merchantID || "",
        IS_RTK_ENABLED: isRtkSearchItemsEnabled,
        FROM_RTK_QUERY: false,
        FROM_OLD_REDUX: true,
        ACTION_NAME: "searchItems",
      },
    })
  }
}

/**
 * Runs getTranslation via RTK Query or legacy Redux, dispatching GET_TRANSLATION
 * optimistic / success / fail shapes for the RTK path so they stay aligned with the reducer.
 * Mirrors the CategoryDrawer / ItemDrawer / ModifierGroups migration pattern.
 */
export function fetchGetTranslation(options: FetchGetTranslationOptions): void {
  const {
    getTranslationBaseAction,
    dispatch,
    data,
    merchantID,
    merchantGroupID,
    menuGroupID,
    isRtkFlagLoading,
    isRtkGetTranslationEnabled,
    getTranslationRtk,
    dispatchLegacyGetTranslation,
  } = options

  if (!isRtkFlagLoading && isRtkGetTranslationEnabled) {
    const keys = generateAutoTranslationQueueKeys(data)
    dispatch({ type: getTranslationBaseAction, params: data, keys })
    getTranslationRtk({
      merchantID,
      merchantGroupID,
      menuGroupID,
      ...data,
    }).then(result => {
      if (result.error) {
        dispatch({
          type: getActionFail(getTranslationBaseAction),
          payload: { data: {} },
          error: {
            response: { data: (result.error as { data?: unknown })?.data },
          },
          meta: {
            previousAction: {
              type: getTranslationBaseAction,
              params: data,
              keys,
            },
          },
        })
        return
      }

      dispatch({
        type: getActionSuccess(getTranslationBaseAction),
        payload: { data: result?.data ?? {} },
        meta: {
          previousAction: {
            type: getTranslationBaseAction,
            params: data,
            keys,
          },
        },
      })
    })
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.getTranslation,
      labels: {
        MERCHANT_ID: merchantID || "",
        IS_RTK_ENABLED: isRtkGetTranslationEnabled,
        FROM_RTK_QUERY: true,
        FROM_OLD_REDUX: false,
        ACTION_NAME: "getTranslation",
      },
    })
  } else if (!isRtkFlagLoading) {
    dispatchLegacyGetTranslation()
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.getTranslation,
      labels: {
        MERCHANT_ID: merchantID || "",
        IS_RTK_ENABLED: isRtkGetTranslationEnabled,
        FROM_RTK_QUERY: false,
        FROM_OLD_REDUX: true,
        ACTION_NAME: "getTranslation",
      },
    })
  }
}

/** RTK `updateItemsAvailableV1` mutation trigger result shape */
export type UpdateItemsAvailableStatusRtkResult = {
  data?: FoodmaxapipbUpdateItemAvailableStatusV2Response
  error?: unknown
}

export interface UpdateItemsAvailableStatusOptions {
  dispatch: (action: unknown) => unknown | Promise<unknown>
  merchantID: string
  merchantGroupID: string
  menuGroupID?: string
  isRtkFlagLoading: boolean
  isRtkUpdateItemsAvailableStatusEnabled: boolean
  updateItemsAvailableV1Rtk: (
    arg: UpdateItemsAvailableV1Request,
  ) => Promise<UpdateItemsAvailableStatusRtkResult>
  itemIDs: string[]
  availableStatus: number
  successMessage: string
  isMartDepartmentStructure: boolean
  isSellingTimeStructure: boolean
  enablePartialMenu: boolean
  /** An RTK action to dispatch on success (e.g. menuApi.util.invalidateTags([...])) */
  invalidateTagsAction?: unknown
}

/**
 * Runs updateItemsAvailableStatus via RTK Query or legacy Redux.
 * On the RTK path: calls the mutation, syncs updated items into Redux, shows a
 * success notification, and dispatches a refreshMenu action (fetchMenu or
 * fetchCatalogue) according to the store structure.
 * On the legacy path: dispatches the Redux action creator which handles the
 * notification, then syncs updated items into Redux.
 * Both paths fire the rtkMigration TroyWebTracker event.
 * Returns a result object so callers can fire submitItemAvailability tracking
 * with the appropriate STATUS and error labels.
 */
export async function updateItemsAvailableStatus(
  options: UpdateItemsAvailableStatusOptions,
): Promise<{ succeeded: boolean; rtkError?: unknown; legacyResp?: any }> {
  const {
    dispatch,
    merchantID,
    merchantGroupID,
    menuGroupID,
    isRtkFlagLoading,
    isRtkUpdateItemsAvailableStatusEnabled,
    updateItemsAvailableV1Rtk,
    itemIDs,
    availableStatus,
    successMessage,
    isMartDepartmentStructure,
    isSellingTimeStructure,
    enablePartialMenu,
    invalidateTagsAction,
  } = options

  if (!isRtkFlagLoading && isRtkUpdateItemsAvailableStatusEnabled) {
    const result = await updateItemsAvailableV1Rtk({
      merchantID,
      merchantGroupID,
      itemIDs,
      availableStatus,
    })

    if (!result.error) {
      const updatedItems = (compact(
        result.data?.items || [],
      ) as unknown) as Item[]

      if (updatedItems.length > 0) {
        dispatch(actionCreator.menu.syncSearchItems(updatedItems))
      }

      notification.success({ message: successMessage })

      if (!isMartDepartmentStructure) {
        dispatch(
          actionCreator.menu.fetchMenu({
            merchantID,
            isRefreshing: true,
            isSellingTimeStructure,
            merchantGroupID,
            menuGroupID,
          }),
        )
      } else if (!enablePartialMenu) {
        dispatch(
          actionCreator.menu.fetchCatalogue({
            merchantID,
            isRefreshing: true,
            isSellingTimeStructure,
            merchantGroupID,
            menuGroupID,
          }),
        )
      }
    }

    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.updateItemsAvailableStatus,
      labels: {
        MERCHANT_ID: merchantID,
        IS_RTK_ENABLED: isRtkUpdateItemsAvailableStatusEnabled,
        FROM_RTK_QUERY: true,
        FROM_OLD_REDUX: false,
        ACTION_NAME: "updateItemsAvailableStatus",
      },
    })

    return result.error
      ? { succeeded: false, rtkError: result.error }
      : { succeeded: true }
    // While migration flags are loading, do not fall back to old Redux: the enabled bit is
    // unknown until GrabX resolves. Legacy-then-RTK for the same user intent can double-call
    // APIs/middleware and splits RTK migration tracking. Wait for !isRtkFlagLoading; disable
    // the trigger UI while loading so clicks are not silently dropped.
  }
  if (!isRtkFlagLoading) {
    const resp: any = await dispatch(
      actionCreator.menu.updateItemsAvailableStatus({
        merchantID,
        itemIDs,
        availableStatus,
        successMessage,
      }),
    )

    if (resp?.type.endsWith("_SUCCESS")) {
      const updatedItems = compact(resp.payload?.data?.items || []) as Item[]

      if (updatedItems.length > 0) {
        dispatch(actionCreator.menu.syncSearchItems(updatedItems))
      }

      if (invalidateTagsAction) {
        dispatch(invalidateTagsAction)
      }
    }

    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.updateItemsAvailableStatus,
      labels: {
        MERCHANT_ID: merchantID,
        IS_RTK_ENABLED: isRtkUpdateItemsAvailableStatusEnabled,
        FROM_RTK_QUERY: false,
        FROM_OLD_REDUX: true,
        ACTION_NAME: "updateItemsAvailableStatus",
      },
    })

    return resp?.type.endsWith("_FAIL")
      ? { succeeded: false, legacyResp: resp }
      : { succeeded: true }
  }

  return { succeeded: false }
}

/** RTK lazy `getBulkTasks` trigger result shape */
export type GetBulkTasksRtkResult = {
  data?: GetBulkUploadTaskResponse
  error?: unknown
}

export interface FetchBulkTasksOptions {
  dispatch: (action: unknown) => unknown | Promise<unknown>
  merchantID: string
  merchantGroupID: string
  requestParams: GetBulkUploadTaskRequest & { menuGroupID?: string }
  isRtkFlagLoading: boolean
  isRtkFetchBulkTasksEnabled: boolean
  fetchBulkTasksRtk: (
    arg: GetBulkUploadTaskRequest & {
      merchantID: string
      merchantGroupID: string
    },
  ) => Promise<GetBulkTasksRtkResult>
  dispatchLegacyFetchBulkTasks: () => unknown | Promise<unknown>
}

/**
 * Runs fetchBulkTasks via RTK Query or legacy Redux, dispatching hand-rolled
 * FETCH_BULK_TASKS pending / success / fail shapes for the RTK path so they
 * stay aligned with the reducer. Does nothing while `isRtkFlagLoading` is true.
 */
export async function fetchBulkTasks(
  options: FetchBulkTasksOptions,
): Promise<void> {
  const {
    dispatch,
    merchantID,
    merchantGroupID,
    requestParams,
    isRtkFlagLoading,
    isRtkFetchBulkTasksEnabled,
    fetchBulkTasksRtk,
    dispatchLegacyFetchBulkTasks,
  } = options

  if (!isRtkFlagLoading && isRtkFetchBulkTasksEnabled) {
    const pendingAction = {
      type: actionTypes.FETCH_BULK_TASKS,
      meta: { requestParams },
    }
    dispatch(pendingAction)

    const result = await fetchBulkTasksRtk({
      merchantID,
      merchantGroupID,
      ...requestParams,
    })

    if (result.error) {
      dispatch({
        type: getActionFail(actionTypes.FETCH_BULK_TASKS),
        meta: { previousAction: pendingAction },
      })
    } else {
      dispatch({
        type: getActionSuccess(actionTypes.FETCH_BULK_TASKS),
        payload: { data: result.data ?? { tasks: [], nextCursor: "" } },
        meta: { previousAction: pendingAction },
      })
    }

    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.fetchBulkTasks,
      labels: {
        MERCHANT_ID: merchantID,
        IS_RTK_ENABLED: isRtkFetchBulkTasksEnabled,
        FROM_RTK_QUERY: true,
        FROM_OLD_REDUX: false,
        ACTION_NAME: "fetchBulkTasks",
      },
    })
  } else if (!isRtkFlagLoading) {
    await Promise.resolve(dispatchLegacyFetchBulkTasks())

    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.fetchBulkTasks,
      labels: {
        MERCHANT_ID: merchantID,
        IS_RTK_ENABLED: isRtkFetchBulkTasksEnabled,
        FROM_RTK_QUERY: false,
        FROM_OLD_REDUX: true,
        ACTION_NAME: "fetchBulkTasks",
      },
    })
  }
}

/**
 * Runs updateExistingTranslation via RTK Query or legacy Redux, dispatching
 * UPDATE_EXISTING_TRANSLATION success / fail shapes for the RTK path so they
 * stay aligned with the reducer. Call only after the parent has resolved
 * `!isRtkFlagLoading` (see TranslationDrawer / ModifierGroups).
 */
export function fetchUpdateExistingTranslation(
  options: FetchUpdateExistingTranslationOptions,
): void {
  const {
    dispatch,
    data,
    merchantID,
    merchantGroupID,
    menuGroupID,
    isRtkUpdateExistingTranslationEnabled,
    updateExistingTranslationRtk,
  } = options

  const updateExistingTranslationBaseAction =
    actions.UPDATE_EXISTING_TRANSLATION

  if (isRtkUpdateExistingTranslationEnabled) {
    // Keys include a timestamp; reuse one array for pending + success/fail so
    // autoTranslationQueue $remove matches the $add from the initial dispatch.
    const autoTranslationQueueKeys = generateAutoTranslationQueueKeys(data)

    dispatch({
      type: updateExistingTranslationBaseAction,
      params: data,
      keys: autoTranslationQueueKeys,
    })

    updateExistingTranslationRtk({
      ...data,
      merchantID,
      merchantGroupID,
      menuGroupID,
    }).then(result => {
      const previousAction = {
        type: updateExistingTranslationBaseAction,
        params: data,
        keys: autoTranslationQueueKeys,
        payload: { request: { data } },
      }

      if (result.error) {
        dispatch({
          type: getActionFail(updateExistingTranslationBaseAction),
          payload: { data: {} },
          error: {
            response: { data: (result.error as { data?: unknown })?.data },
          },
          meta: { previousAction },
        })
      } else if (result.data) {
        dispatch({
          type: getActionSuccess(updateExistingTranslationBaseAction),
          payload: { data: result.data },
          meta: { previousAction },
        })
      }

      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.updateExistingTranslation,
        labels: {
          MERCHANT_ID: merchantID || "",
          IS_RTK_ENABLED: isRtkUpdateExistingTranslationEnabled,
          FROM_RTK_QUERY: true,
          FROM_OLD_REDUX: false,
          ACTION_NAME: "updateExistingTranslation",
        },
      })
    })
  } else {
    dispatch(
      actionCreator.menu.updateExistingTranslation({
        data,
        merchantID,
        merchantGroupID,
        menuGroupID,
      }),
    )

    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.updateExistingTranslation,
      labels: {
        MERCHANT_ID: merchantID || "",
        IS_RTK_ENABLED: isRtkUpdateExistingTranslationEnabled,
        FROM_RTK_QUERY: false,
        FROM_OLD_REDUX: true,
        ACTION_NAME: "updateExistingTranslation",
      },
    })
  }
}

export interface PostFeedbackGrabCategoryOptions {
  dispatch: (action: unknown) => unknown | Promise<unknown>
  merchantID: string
  merchantGroupID: string
  menuGroupID?: string
  isRtkFlagLoading: boolean
  isRtkPostFeedbackGrabCategoryEnabled: boolean
  predictionFeedback: PostPredictionFeedbacksGrabCategory
  postFeedbackGrabCategoryRtk: (
    arg: PostFeedbackGrabCategoryRequest,
  ) => Promise<unknown>
}

/**
 * Sends prediction feedback for Grab Category via RTK Query or legacy Redux,
 * with TroyWebTracker instrumentation on both paths.
 * Falls back to legacy when RTK flags are still loading so one-shot callers
 * (ItemDrawer save handlers) never silently drop feedback.
 * Callers that must not fire during flag loading (DepartmentTaggingDrawer effect)
 * should guard themselves before calling this helper.
 */
export function postFeedbackGrabCategory(
  options: PostFeedbackGrabCategoryOptions,
): void {
  const {
    dispatch,
    merchantID,
    merchantGroupID,
    menuGroupID,
    isRtkFlagLoading,
    isRtkPostFeedbackGrabCategoryEnabled,
    predictionFeedback,
    postFeedbackGrabCategoryRtk,
  } = options

  const shouldUseRtk = isRtkPostFeedbackGrabCategoryEnabled && !isRtkFlagLoading

  if (shouldUseRtk) {
    postFeedbackGrabCategoryRtk({
      merchantID,
      merchantGroupID,
      menuGroupID,
      predictionFeedback,
    })
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.postFeedbackGrabCategory,
      labels: {
        MERCHANT_ID: merchantID,
        IS_RTK_ENABLED: isRtkPostFeedbackGrabCategoryEnabled,
        FROM_RTK_QUERY: true,
        FROM_OLD_REDUX: false,
        ACTION_NAME: "postFeedbackGrabCategory",
      },
    })
  } else {
    dispatch(
      actionCreator.menu.postFeedbackGrabCategory(
        predictionFeedback,
        merchantID,
        merchantGroupID,
        menuGroupID,
      ),
    )
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.postFeedbackGrabCategory,
      labels: {
        MERCHANT_ID: merchantID,
        IS_RTK_ENABLED: isRtkPostFeedbackGrabCategoryEnabled,
        FROM_RTK_QUERY: false,
        FROM_OLD_REDUX: true,
        ACTION_NAME: "postFeedbackGrabCategory",
      },
    })
  }
}

export function fetchSortDepartments(
  options: FetchSortDepartmentsOptions,
): void {
  const {
    dispatch,
    merchantID,
    merchantGroupID,
    menuGroupID,
    parentDepartmentID,
    sorts,
    successMessage,
    isRtkSortDepartmentsEnabled,
    sortDepartmentsRtk,
  } = options

  const sendTracking = (fromRtkQuery: boolean) => {
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.sortDepartments,
      labels: {
        MERCHANT_ID: merchantID || "",
        IS_RTK_ENABLED: isRtkSortDepartmentsEnabled,
        FROM_RTK_QUERY: fromRtkQuery,
        FROM_OLD_REDUX: !fromRtkQuery,
        ACTION_NAME: "sortDepartments",
      },
    })
  }

  if (isRtkSortDepartmentsEnabled) {
    sortDepartmentsRtk({
      merchantID,
      merchantGroupID,
      parentDepartmentID,
      sorts,
      menuGroupID,
      showError: true,
      showSuccess: true,
      successOptions: { message: successMessage },
    }).then(result => {
      if (!result.error) {
        refreshMenuAndCloseDrawers(options)
      }

      sendTracking(true)
    })
    return
  }

  dispatch(
    actionCreator.menu.sortDepartments({
      merchantID,
      sorts,
      parentDepartmentID,
      successMessage,
      merchantGroupID,
      menuGroupID,
    }),
  )

  sendTracking(false)
}

export function fetchSortItems(options: FetchSortItemsOptions): void {
  const {
    dispatch,
    merchantID,
    merchantGroupID,
    menuGroupID,
    categoryID,
    sorts,
    successMessage,
    isRtkSortItemsEnabled,
    sortItemsRtk,
  } = options

  const sendTracking = (fromRtkQuery: boolean) => {
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.sortItems,
      labels: {
        MERCHANT_ID: merchantID || "",
        IS_RTK_ENABLED: isRtkSortItemsEnabled,
        FROM_RTK_QUERY: fromRtkQuery,
        FROM_OLD_REDUX: !fromRtkQuery,
        ACTION_NAME: "sortItems",
      },
    })
  }

  if (isRtkSortItemsEnabled) {
    sortItemsRtk({
      merchantID,
      merchantGroupID,
      categoryID,
      sorts,
      menuGroupID,
      showError: true,
      showSuccess: true,
      successOptions: { message: successMessage },
    }).then(result => {
      if (!result.error) {
        refreshMenuAndCloseDrawers(options)
      }

      sendTracking(true)
    })
    return
  }

  dispatch(
    actionCreator.menu.sortItems({
      merchantID,
      categoryID,
      sorts,
      successMessage,
      merchantGroupID,
      menuGroupID,
    }),
  )

  sendTracking(false)
}

export function fetchSortCategories(options: FetchSortCategoriesOptions): void {
  const {
    dispatch,
    merchantID,
    merchantGroupID,
    menuGroupID,
    sectionID,
    sorts,
    successMessage,
    isRtkSortCategoriesEnabled,
    sortCategoriesRtk,
  } = options

  const sendTracking = (fromRtkQuery: boolean) => {
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.sortCategories,
      labels: {
        MERCHANT_ID: merchantID || "",
        IS_RTK_ENABLED: isRtkSortCategoriesEnabled,
        FROM_RTK_QUERY: fromRtkQuery,
        FROM_OLD_REDUX: !fromRtkQuery,
        ACTION_NAME: "sortCategories",
      },
    })
  }

  if (isRtkSortCategoriesEnabled) {
    sortCategoriesRtk({
      merchantID,
      merchantGroupID,
      sectionID,
      sorts,
      menuGroupID,
      showError: true,
      showSuccess: true,
      successOptions: { message: successMessage },
    }).then(result => {
      if (!result.error) {
        refreshMenuAndCloseDrawers(options)
      }

      sendTracking(true)
    })
    return
  }

  dispatch(
    actionCreator.menu.sortCategories({
      merchantID,
      sectionID,
      sorts,
      successMessage,
      merchantGroupID,
      menuGroupID,
    }),
  )

  sendTracking(false)
}

export async function fetchCreateSection(
  options: FetchCreateSectionOptions,
): Promise<FetchCreateSectionResult | undefined> {
  const {
    dispatch,
    merchantID,
    merchantGroupID,
    menuGroupID,
    data,
    successMessage,
    errorCallback,
    getErrorNotificationContent,
    isRtkCreateSectionEnabled,
    createSectionRtk,
  } = options

  const sendTracking = (fromRtkQuery: boolean) => {
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.createSection,
      labels: {
        MERCHANT_ID: merchantID || "",
        IS_RTK_ENABLED: isRtkCreateSectionEnabled,
        FROM_RTK_QUERY: fromRtkQuery,
        FROM_OLD_REDUX: !fromRtkQuery,
        ACTION_NAME: "createSection",
      },
    })
  }

  if (isRtkCreateSectionEnabled) {
    const result = await createSectionRtk({
      merchantID,
      merchantGroupID,
      menuGroupID,
      sectionToCreate: data.sectionToCreate,
      sectionsToUpdate: data.sectionsToUpdate,
      getErrorNotificationContent,
      showSuccess: true,
      successOptions: {
        message: successMessage,
      },
    })

    if (!result.error && result.data) {
      const sectionID = result.data?.sectionID

      await dispatch(
        actionCreator.menu.fetchMenu({
          merchantID,
          menuGroupID,
          merchantGroupID,
          isSellingTimeStructure: false,
        }),
      )
      dispatch(actionCreator.menu.selectSectionByID(sectionID ?? ""))

      dispatch(actionCreator.bulkSelectionsV3.reset())
      dispatch(
        actionCreator.menu.toggleDrawer({
          showSection: false,
          showCategory: false,
          showSubDepartment: false,
          showItem: false,
          showSortCategories: false,
          showSortItems: false,
          showSortDepartments: false,
          showSortSubDepartments: false,
          showSellingTimeEditor: false,
          showMultiSelectSellingTime: false,
          showItemSelection: false,
          itemSelections: {},
        }),
      )
    }

    sendTracking(true)

    return { fromRtkQuery: true, result }
  }

  const result = (await dispatch(
    actionCreator.menu.createSection({
      merchantID,
      data,
      successMessage,
      errorCallback,
      menuGroupID,
      merchantGroupID,
    }),
  )) as AxiosAction<CreateSectionResponse> | undefined

  if (result?.type.endsWith("_SUCCESS")) {
    const sectionID = result?.payload?.data?.sectionID

    await dispatch(
      actionCreator.menu.fetchMenu({
        merchantID,
        menuGroupID,
        merchantGroupID,
        isSellingTimeStructure: false,
      }),
    )
    dispatch(actionCreator.menu.selectSectionByID(sectionID ?? ""))
  }

  sendTracking(false)

  if (!result) return undefined

  return { fromRtkQuery: false, result }
}

export async function fetchCreateCategory(
  options: FetchCreateCategoryOptions,
): Promise<FetchCreateCategoryResult | undefined> {
  const {
    dispatch,
    merchantID,
    merchantGroupID,
    menuGroupID,
    data,
    isSellingTimeStructure,
    successOptions,
    errorCallback,
    getErrorNotificationContent,
    isRtkCreateCategoryEnabled,
    createCategoryRtk,
  } = options

  const sendTracking = (fromRtkQuery: boolean) => {
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.createCategory,
      labels: {
        MERCHANT_ID: merchantID || "",
        IS_RTK_ENABLED: isRtkCreateCategoryEnabled,
        FROM_RTK_QUERY: fromRtkQuery,
        FROM_OLD_REDUX: !fromRtkQuery,
        ACTION_NAME: "createCategory",
      },
    })
  }

  if (isRtkCreateCategoryEnabled) {
    const result = await createCategoryRtk({
      merchantID,
      merchantGroupID,
      menuGroupID,
      data,
      getErrorNotificationContent,
      showSuccess: true,
      successOptions,
    })

    if (!result.error && result.data) {
      const categoryID = result.data.categoryID

      await dispatch(
        actionCreator.menu.fetchMenu({
          merchantID,
          isSellingTimeStructure,
          merchantGroupID,
          menuGroupID,
        }),
      )
      dispatch(actionCreator.menu.selectCategoryByID(categoryID ?? ""))
      dispatch(actionCreator.bulkSelectionsV3.reset())
      dispatch(
        actionCreator.menu.toggleDrawer({
          showSection: false,
          showCategory: false,
          showSubDepartment: false,
          showItem: false,
          showSortCategories: false,
          showSortItems: false,
          showSortDepartments: false,
          showSortSubDepartments: false,
          showSellingTimeEditor: false,
          showMultiSelectSellingTime: false,
          showItemSelection: false,
          itemSelections: {},
        }),
      )
    }

    sendTracking(true)

    return { fromRtkQuery: true, result }
  }

  const result = (await dispatch(
    actionCreator.menu.createCategory({
      merchantID,
      data,
      errorCallback,
      successOptions,
      menuGroupID,
      merchantGroupID,
    }),
  )) as AxiosAction<CreateCategoryResponse> | undefined

  if (result?.type.endsWith("_SUCCESS")) {
    const categoryID = result.payload?.data?.categoryID

    await dispatch(
      actionCreator.menu.fetchMenu({
        merchantID,
        isSellingTimeStructure,
        merchantGroupID,
        menuGroupID,
      }),
    )
    dispatch(actionCreator.menu.selectCategoryByID(categoryID ?? ""))
  }

  sendTracking(false)

  if (!result) return undefined

  return { fromRtkQuery: false, result }
}

export function fetchSortDepartmentItems(
  options: FetchSortDepartmentItemsOptions,
): void {
  const {
    dispatch,
    merchantID,
    merchantGroupID,
    menuGroupID,
    departmentID,
    sorts,
    successMessage,
    isRtkSortDepartmentItemsEnabled,
    sortDepartmentItemsRtk,
  } = options

  const sendTracking = (fromRtkQuery: boolean) => {
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.sortDepartmentItems,
      labels: {
        MERCHANT_ID: merchantID || "",
        IS_RTK_ENABLED: isRtkSortDepartmentItemsEnabled,
        FROM_RTK_QUERY: fromRtkQuery,
        FROM_OLD_REDUX: !fromRtkQuery,
        ACTION_NAME: "sortDepartmentItems",
      },
    })
  }

  if (isRtkSortDepartmentItemsEnabled) {
    sortDepartmentItemsRtk({
      merchantID,
      merchantGroupID,
      departmentID,
      sorts,
      menuGroupID,
      showError: true,
      showSuccess: true,
      successOptions: { message: successMessage },
    }).then(result => {
      if (!result.error) {
        refreshMenuAndCloseDrawers(options)
      }

      sendTracking(true)
    })
    return
  }

  dispatch(
    actionCreator.menu.sortDepartmentItems({
      merchantID,
      departmentID,
      sorts,
      successMessage,
      merchantGroupID,
      menuGroupID,
    }),
  )

  sendTracking(false)
}

export interface FetchSupportedAttributeClusterOptions
  extends RtkMigrationBaseOptions {
  /** Base action type, e.g. `actions.FETCH_SUPPORTED_ATTRIBUTE_CLUSTER` from the menu duck */
  fetchSupportedAttributeClusterBaseAction: string
  fetchSupportedAttributeClusterRequest: FetchSupportedAttributeClusterRequest
  isRtkFetchSupportedAttributeClusterEnabled: boolean
  fetchSupportedAttributeClusterRtk: (
    arg: FetchSupportedAttributeClusterRequest,
  ) => Promise<FetchSupportedAttributeClusterRtkResult>
  dispatchLegacyFetch: () => void
}

/**
 * Runs fetchSupportedAttributeCluster via RTK Query or legacy Redux, dispatching hand-rolled
 * FETCH_SUPPORTED_ATTRIBUTE_CLUSTER_SUCCESS / FAIL shapes for the RTK path so they stay aligned with the reducer.
 * Mirrors the DepartmentTaggingDrawer / SubDepartmentTaggingDrawer / ItemDrawer migration pattern.
 */
export async function fetchSupportedAttributeCluster(
  options: FetchSupportedAttributeClusterOptions,
): Promise<void> {
  const {
    fetchSupportedAttributeClusterBaseAction,
    dispatch,
    merchantID,
    isRtkFlagLoading,
    isRtkFetchSupportedAttributeClusterEnabled,
    fetchSupportedAttributeClusterRequest,
    fetchSupportedAttributeClusterRtk,
    dispatchLegacyFetch,
  } = options

  if (!isRtkFlagLoading && isRtkFetchSupportedAttributeClusterEnabled) {
    dispatch({ type: fetchSupportedAttributeClusterBaseAction })
    const result = await fetchSupportedAttributeClusterRtk(
      fetchSupportedAttributeClusterRequest,
    )

    if (result.error) {
      dispatch({
        type: getActionFail(fetchSupportedAttributeClusterBaseAction),
        payload: { data: {} },
        meta: {
          previousAction: {
            type: fetchSupportedAttributeClusterBaseAction,
          },
        },
      })
    } else {
      dispatch({
        type: getActionSuccess(fetchSupportedAttributeClusterBaseAction),
        payload: { data: result.data },
        meta: {
          previousAction: {
            type: fetchSupportedAttributeClusterBaseAction,
          },
        },
      })
    }

    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.fetchSupportedAttributeCluster,
      labels: {
        MERCHANT_ID: merchantID || "",
        IS_RTK_ENABLED: isRtkFetchSupportedAttributeClusterEnabled,
        FROM_RTK_QUERY: true,
        FROM_OLD_REDUX: false,
        ACTION_NAME: "fetchSupportedAttributeCluster",
      },
    })
  } else if (!isRtkFlagLoading) {
    dispatchLegacyFetch()
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.fetchSupportedAttributeCluster,
      labels: {
        MERCHANT_ID: merchantID || "",
        IS_RTK_ENABLED: isRtkFetchSupportedAttributeClusterEnabled,
        FROM_RTK_QUERY: false,
        FROM_OLD_REDUX: true,
        ACTION_NAME: "fetchSupportedAttributeCluster",
      },
    })
  }
}
